import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Block as DeactivateIcon,
  CheckCircle as ActivateIcon,
  Edit as EditIcon,
  LockReset as ResetIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { useAuthStore } from '../../store/authStore';
import {
  listTenantUsers,
  createTenantUser,
  setTenantUserActive,
  updateTenantUser,
  resetTenantUserPassword,
  deleteTenantUser,
  TenantUser,
  TenantUserRole,
} from '../../services/tenantAdmin';

const ROLE_OPTIONS: { code: TenantUserRole; label: string }[] = [
  { code: 'user', label: 'User' },
  { code: 'tenant_admin', label: 'Tenant Admin' },
];

const roleLabel = (role: string) => ROLE_OPTIONS.find((r) => r.code === role)?.label ?? role;

const emptyCreate = {
  email: '',
  username: '',
  firstname: '',
  lastname: '',
  password: '',
  role: 'user' as TenantUserRole,
};

/** Tenant-admin user management — used as a tab inside the Settings page. */
const TenantUsersSection: React.FC = () => {
  const currentUser = useAuthStore((s) => s.user);
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [isCreating, setIsCreating] = useState(false);

  const [editUser, setEditUser] = useState<TenantUser | null>(null);
  const [editForm, setEditForm] = useState({ firstname: '', lastname: '', email: '', phonenumber: '' });
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [pwdUser, setPwdUser] = useState<TenantUser | null>(null);
  const [pwd, setPwd] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  const [deleteUser, setDeleteUser] = useState<TenantUser | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await listTenantUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load users');
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const activeAdminCount = users.filter((u) => u.role === 'tenant_admin' && u.is_active).length;
  const isLastAdmin = (u: TenantUser) =>
    u.role === 'tenant_admin' && u.is_active && activeAdminCount <= 1;

  const handleCreate = async () => {
    if (!createForm.email.trim() || !createForm.username.trim() || !createForm.password) {
      toast.error('Email, username and password are required');
      return;
    }
    try {
      setIsCreating(true);
      await createTenantUser({
        email: createForm.email.trim(),
        username: createForm.username.trim(),
        firstname: createForm.firstname.trim(),
        lastname: createForm.lastname.trim(),
        password: createForm.password,
        role: createForm.role,
      });
      toast.success('User created successfully');
      setCreateOpen(false);
      setCreateForm(emptyCreate);
      loadUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleActive = async (user: TenantUser) => {
    const next = !user.is_active;
    try {
      await setTenantUserActive(user.id, next);
      toast.success(`${user.username} ${next ? 'activated' : 'deactivated'}`);
      loadUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleRoleChange = async (user: TenantUser, role: TenantUserRole) => {
    if (role === user.role) return;
    if (currentUser?.id === user.id) {
      toast.error("You can't change your own role");
      return;
    }
    try {
      await updateTenantUser(user.id, { role });
      toast.success(`${user.username} is now ${roleLabel(role)}`);
      loadUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to change role');
      loadUsers();
    }
  };

  const openEdit = (user: TenantUser) => {
    setEditUser(user);
    setEditForm({
      firstname: user.firstname || '',
      lastname: user.lastname || '',
      email: user.email || '',
      phonenumber: user.phonenumber || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editUser) return;
    if (!editForm.email.trim()) {
      toast.error('Email is required');
      return;
    }
    try {
      setIsSavingEdit(true);
      await updateTenantUser(editUser.id, {
        firstname: editForm.firstname.trim(),
        lastname: editForm.lastname.trim(),
        email: editForm.email.trim(),
        phonenumber: editForm.phonenumber.trim(),
      });
      toast.success('User updated');
      setEditUser(null);
      loadUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleResetPassword = async () => {
    if (!pwdUser) return;
    if (pwd.trim().length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    try {
      setIsResetting(true);
      await resetTenantUserPassword(pwdUser.id, pwd);
      toast.success(`Password reset for ${pwdUser.username}`);
      setPwdUser(null);
      setPwd('');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsResetting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    try {
      setIsDeleting(true);
      const res = await deleteTenantUser(deleteUser.id);
      toast.success(res.action === 'deactivated' ? 'User deactivated (has history)' : 'User deleted');
      setDeleteUser(null);
      loadUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight={600}>
          User Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadUsers}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Create User
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                      No users found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => {
                  const lastAdmin = isLastAdmin(user);
                  const isSelf = currentUser?.id === user.id;
                  return (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {user.username}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {user.firstname} {user.lastname}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                          <Select
                            value={user.role === 'tenant_admin' ? 'tenant_admin' : 'user'}
                            onChange={(e) => handleRoleChange(user, e.target.value as TenantUserRole)}
                            disabled={lastAdmin || isSelf}
                          >
                            {ROLE_OPTIONS.map((r) => (
                              <MenuItem key={r.code} value={r.code}>
                                {r.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.is_active ? 'Active' : 'Inactive'}
                          color={user.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={isSelf ? "You can't deactivate yourself" : user.is_active ? 'Deactivate' : 'Reactivate'}>
                          <span>
                            <IconButton
                              size="small"
                              color={user.is_active ? 'warning' : 'success'}
                              onClick={() => handleToggleActive(user)}
                              disabled={isSelf || (user.is_active && lastAdmin)}
                            >
                              {user.is_active ? <DeactivateIcon /> : <ActivateIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit profile">
                          <IconButton size="small" color="primary" onClick={() => openEdit(user)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reset password">
                          <IconButton size="small" color="primary" onClick={() => { setPwdUser(user); setPwd(''); }}>
                            <ResetIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isSelf ? "You can't delete yourself" : lastAdmin ? "Can't delete the last admin" : 'Delete user'}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteUser(user)}
                              disabled={isSelf || lastAdmin}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create User Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create User</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <TextField label="Email" type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} fullWidth autoFocus />
            <TextField label="Username" value={createForm.username} onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })} fullWidth />
            <Box display="flex" gap={2}>
              <TextField label="First name" value={createForm.firstname} onChange={(e) => setCreateForm({ ...createForm, firstname: e.target.value })} fullWidth />
              <TextField label="Last name" value={createForm.lastname} onChange={(e) => setCreateForm({ ...createForm, lastname: e.target.value })} fullWidth />
            </Box>
            <TextField label="Password" type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} fullWidth />
            <FormControl fullWidth>
              <InputLabel id="create-role-label">Role</InputLabel>
              <Select labelId="create-role-label" label="Role" value={createForm.role} onChange={(e) => setCreateForm({ ...createForm, role: e.target.value as TenantUserRole })}>
                {ROLE_OPTIONS.map((r) => (
                  <MenuItem key={r.code} value={r.code}>{r.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={isCreating}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={isCreating}>{isCreating ? 'Creating...' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onClose={() => setEditUser(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit User{editUser ? ` — ${editUser.username}` : ''}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <Box display="flex" gap={2}>
              <TextField label="First name" value={editForm.firstname} onChange={(e) => setEditForm({ ...editForm, firstname: e.target.value })} fullWidth />
              <TextField label="Last name" value={editForm.lastname} onChange={(e) => setEditForm({ ...editForm, lastname: e.target.value })} fullWidth />
            </Box>
            <TextField label="Email" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} fullWidth />
            <TextField label="Phone number" value={editForm.phonenumber} onChange={(e) => setEditForm({ ...editForm, phonenumber: e.target.value })} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUser(null)} disabled={isSavingEdit}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={isSavingEdit}>{isSavingEdit ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!pwdUser} onClose={() => setPwdUser(null)} fullWidth maxWidth="xs">
        <DialogTitle>Reset Password{pwdUser ? ` — ${pwdUser.username}` : ''}</DialogTitle>
        <DialogContent>
          <TextField
            label="New password"
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            fullWidth
            autoFocus
            helperText="At least 8 characters"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwdUser(null)} disabled={isResetting}>Cancel</Button>
          <Button variant="contained" onClick={handleResetPassword} disabled={isResetting}>{isResetting ? 'Resetting...' : 'Reset'}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteUser} onClose={() => setDeleteUser(null)} maxWidth="xs">
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteUser?.username}</strong>? This cannot be undone. A user with
            processing history will be deactivated instead.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUser(null)} disabled={isDeleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={isDeleting}>{isDeleting ? 'Deleting...' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantUsersSection;
